/**
 * The customer is managed from its page (migration 127600; the owner, 2026-10-06). The ERP owns every customer field.
 *
 *   · Status: Active / Pause (from, until or until further notice) / Left Desigo (from, a CUSTOMER_LEFT reason; Other
 *     needs a note) — api.set_customer_status, one dated command; the header chip says what is and what is scheduled.
 *   · The arrangement from a date: items and quantities (api.change_customer_variant) and the days (api.set_customer_pattern,
 *     daily / alternate / chosen weekdays). The day must still be open for the customer (before its cut-off); the server
 *     says so when it is not. Dated changes still waiting are listed with their dates (customer_360.pending_changes).
 *   · A new or moved door from a date (api.move_customer_door), optionally closing the old door that day — customer.edit.
 *   · Discounts: ₹ off an item, or % off the bill, each from a date (api.set_customer_discount / end_customer_discount).
 * Each drawer keeps one idempotency key while it is open; the server's refusal is shown as its sentence.
 */
import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { AlertTriangle, CalendarClock, CircleSlash, Contact, MapPinPlus, PauseCircle, Percent, Plus, Repeat, Trash2, UserCog } from 'lucide-react';
import { rpc, supabase } from '../../lib/supabase';
import { useSession } from '../../lib/session';
import { Dialog, Drawer } from '../../ui/overlays';
import { Button, StatusBadge, cx, plainError, type Tone } from '../../ui/primitives';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/forms';
import { toast } from '../../ui/toast';
import { addDays, inr, istToday, useCalendarRange, useCutoffState } from './customerReads';
import { useBusinessDay } from '../../lib/businessDay';
import { useOpenTab } from './CustomerViews';
import { isTomorrowCut } from './changeBasket';
import type { Customer360 } from './customerDescriptor';
import { useIdempotencyKey } from '../../lib/idempotency';
import { EVERY_OPTIONS, everyLabel, isoWeekday, type Every } from '../../lib/itemDays';

/* ── What customer_360 carries since 127600 ─────────────────────────────────────────────────────────── */

export interface StatusDetail {
  status: 'STARTING' | 'ACTIVE' | 'PAUSED' | 'INACTIVE' | 'RETIRED' | 'LEFT';
  stored: string; from: string | null; until: string | null;
  reasonCode: string | null; reasonLabel: string | null; reasonLabelHi: string | null; note: string | null;
  scheduled: { id: string; status: string; from: string; until: string | null; reasonCode: string | null; note: string | null; auto: boolean } | null;
}
export interface CustomerDiscount {
  id: string; kind: 'VARIANT_RUPEES' | 'BILL_PERCENT'; variantId: string | null; variant: string | null;
  amountPaise: number | null; percent: number | null; from: string; until: string | null; reason: string; state: 'LIVE' | 'SCHEDULED';
}
export interface CustomerPattern { subscriptionId: string; pattern: string; stored: string; weekdays: number[] | null }
export interface PendingChanges {
  agreements: { subscriptionId: string; from: string; replaces: string; pattern: string; weekdays: number[] | null; doorChanged: boolean; door: string | null;
    lines: { variant: string; quantity: number; cycle: number[] | null }[] }[];
  doors: { moveId: string; from: string; doorId: string; closesDoorId: string | null; routeId: string | null }[];
  statuses: { id: string; status: string; from: string; until: string | null }[];
}
export interface UnpaidWarning { amountPaise: number; months: number; days: number; oldestMonth: string }

/* ── Small shared parts ─────────────────────────────────────────────────────────────────────────────── */

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
/** "10 Oct". */
export const dm = (d: string | null | undefined) => (d ? new Date(`${d.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : '—');
/** "10–15 Oct", or "28 Oct – 3 Nov" across a month. */
const span = (from: string, until: string) => (from.slice(0, 7) === until.slice(0, 7) ? `${Number(from.slice(8, 10))}–${dm(until)}` : `${dm(from)} – ${dm(until)}`);
const weekdayList = (w: readonly number[] | null | undefined) => (w ?? []).slice().sort().map((i) => WEEKDAYS[i - 1]).join(', ');
export function patternLabel(pattern: string | null | undefined, weekdays?: readonly number[] | null, lines?: readonly { cycle: number[] | null }[]) {
  const p = (pattern ?? '').toUpperCase();
  if (p === 'DAILY') return 'every day';
  if (p === 'ALTERNATE') return 'alternate days';
  if (p === 'WEEKDAYS') return `on ${weekdayList(weekdays)}`;
  if (p === 'CYCLE') return lines && lines.length && lines.every((l) => l.cycle?.length === 2 && l.cycle[1] === 0) ? 'alternate days' : 'a cycle of days';
  return p.toLowerCase() || '—';
}
const sentence = (e: unknown) => plainError((e as Error)?.message ?? String(e));

interface Reason { code: string; label_en: string; requires_comment: boolean }
/** A reason list, read as Change delivery reads it (same key, same columns). */
function useReasonCodes(domain: string) {
  return useQuery({
    queryKey: ['reason-codes', domain], staleTime: 600_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('reason_codes').select('code,label_en,requires_comment,sort_order,status')
        .eq('domain', domain).eq('status', 'ACTIVE').order('sort_order');
      if (error) throw error;
      return (data ?? []) as Reason[];
    },
  });
}
interface Variant { id: string; code: string; name: string }
/** The active variants, as Change delivery reads them (same key). */
function useVariants() {
  return useQuery({
    queryKey: ['variant-ids'], staleTime: 600_000,
    queryFn: async () => {
      const { data, error } = await supabase.from('product_variants').select('id,code,name,status').order('code');
      if (error) throw error;
      return (data ?? []).filter((v: { status: string }) => v.status === 'ACTIVE') as Variant[];
    },
  });
}

/** After a change: the page's own record, its calendar, its money and its history read again. */
export const refreshCustomer = (qc: QueryClient, customerId: string) => qc.invalidateQueries({
  predicate: (q) => (q.queryKey[0] === 'customers' && q.queryKey[1] === '360' && q.queryKey[2] === customerId)
    || ['customer-agreement', 'customer-calendar', 'customer-health', 'customer-timeline', 'customer-doors', 'customer-bill', 'customer-bill-days', 'customer-balance', 'customer-delivery-facts', 'entity-timeline']
      .includes(String(q.queryKey[0])),
});

/** The first day a dated change can take: tomorrow, or the day after once tomorrow is prepared or its cut-off has passed (the server's word, 145550). */
function useFirstOpenDay() {
  const cutoff = useCutoffState();
  const business = useBusinessDay();
  const today = istToday();
  const byClock = addDays(today, isTomorrowCut(cutoff.data, today, Date.now()) ? 2 : 1);
  const byServer = business.data?.firstOpenDay?.slice(0, 10);
  return byServer && byServer > byClock ? byServer : byClock;
}

/** The agreements in force on a day (ACTIVE or PAUSED); a choice is asked only when there is more than one. */
const inForce = (entity: Customer360, day: string) => (entity.subscriptions ?? [])
  .filter((s) => (s.status === 'ACTIVE' || s.status === 'PAUSED') && s.start_date.slice(0, 10) <= day && (!s.end_date || s.end_date.slice(0, 10) >= day));

function Outcome({ error }: { error: unknown }) {
  return error ? <p role="alert" className="text-small text-refused">{sentence(error)}</p> : null;
}
function Footer({ onClose, disabled, pending, label }: { onClose: () => void; disabled: boolean; pending: boolean; label: string }) {
  return (
    <div className="flex justify-end gap-2">
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="primary" disabled={disabled || pending} type="submit" form="manage-form">{pending ? 'Working…' : label}</Button>
    </div>);
}
function Choice<T extends string>({ value, options, onChange, label }: { value: T; options: readonly (readonly [T, string])[]; onChange: (v: T) => void; label: string }) {
  return (
    <div role="group" aria-label={label} className="inline-flex flex-wrap gap-1 rounded-[var(--radius-control)] border border-line p-0.5">
      {options.map(([v, l]) => (
        <button key={v} type="button" aria-pressed={value === v} onClick={() => onChange(v)}
          className={cx('h-7 rounded-[var(--radius-control)] px-2.5 text-small', value === v ? 'bg-brand-soft font-medium text-brand' : 'text-ink-muted hover:bg-surface-sunken')}>{l}</button>))}
    </div>);
}
function Form({ onSubmit, children }: { onSubmit: () => void; children: ReactNode }) {
  return <form id="manage-form" className="grid gap-4 px-4 py-4" onSubmit={(e) => { e.preventDefault(); onSubmit(); }}>{children}</form>;
}
function AgreementPick({ entity, day, value, onChange }: { entity: Customer360; day: string; value: string; onChange: (v: string) => void }) {
  const subs = inForce(entity, day);
  if (subs.length < 2) return null;
  return (
    <Field label="Which agreement" required hint="This customer has more than one agreement on that day.">
      <Select value={value} onChange={(e) => onChange(e.target.value)} placeholder="Choose…"
        options={subs.map((s) => ({ value: s.id, label: `${s.code} · ${s.units_per_delivery} unit(s) · ${s.schedule_pattern.toLowerCase()}` }))} />
    </Field>);
}

/* ── Header: the status, what is scheduled, and the unpaid warning ──────────────────────────────────── */

const STATUS_TONE: Record<string, Tone> = { ACTIVE: 'accepted', PAUSED: 'queued', STARTING: 'info', LEFT: 'refused', INACTIVE: 'neutral', RETIRED: 'neutral' };

function statusWords(d: StatusDetail) {
  if (d.status === 'PAUSED') return d.until ? `Paused ${d.from ? span(d.from, d.until) : `until ${dm(d.until)}`}` : `Paused${d.from ? ` from ${dm(d.from)}` : ''} · until further notice`;
  if (d.status === 'LEFT') return `Left Desigo${d.from ? ` ${dm(d.from)}` : ''}${d.reasonLabel ? ` · ${d.reasonLabel}` : ''}`;
  return d.status.charAt(0) + d.status.slice(1).toLowerCase();
}
function scheduledWords(d: StatusDetail) {
  const s = d.scheduled;
  if (!s) return null;
  // The resume a dated pause made for itself is already said by "Paused 10–15 Oct".
  if (s.auto && d.status === 'PAUSED' && d.until) return null;
  if (s.status === 'PAUSED') return s.until ? `Pauses ${span(s.from, s.until)}` : `Pauses from ${dm(s.from)}`;
  if (s.status === 'LEFT') return `Leaves on ${dm(s.from)}`;
  if (s.status === 'ACTIVE') return `Resumes on ${dm(s.from)}`;
  return `${s.status.toLowerCase()} from ${dm(s.from)}`;
}

/** The status chip (with its reason as a tooltip), the change waiting for its date, and money unpaid from past months. */
export function CustomerStatusChips({ entity }: { entity: Customer360 }) {
  const d = entity.status_detail;
  const next = d ? scheduledWords(d) : null;
  const u = entity.unpaid;
  return (
    <>
      {d ? (
        <StatusBadge tone={STATUS_TONE[d.status] ?? 'neutral'} icon={d.status === 'PAUSED' ? PauseCircle : d.status === 'LEFT' ? CircleSlash : Contact}
          title={[d.reasonLabel, d.note].filter(Boolean).join(': ') || undefined}>{statusWords(d)}</StatusBadge>
      ) : (
        <StatusBadge tone={STATUS_TONE[entity.status] ?? 'neutral'} icon={Contact}>{entity.status.toLowerCase()}</StatusBadge>
      )}
      {next ? <StatusBadge tone="queued" icon={CalendarClock} title={d?.scheduled?.note ?? undefined}>{next}</StatusBadge> : null}
      {u && u.amountPaise > 0 ? (
        <StatusBadge tone="refused" icon={AlertTriangle} title={`${u.months} month(s) not paid, the oldest ${u.oldestMonth}. Nothing pauses by itself.`}>
          Unpaid {inr(u.amountPaise)} · {u.days} days
        </StatusBadge>) : null}
    </>);
}

/* ── Status: Active / Pause / Left Desigo ───────────────────────────────────────────────────────────── */

type Target = 'ACTIVE' | 'PAUSED' | 'LEFT';

export function CustomerStatusButton({ entity }: { entity: Customer360 }) {
  const { can } = useSession();
  const [open, setOpen] = useState(false);
  if (!can('customer.status.manage') || entity.status === 'RETIRED') return null;
  return (
    <>
      <Button size="dense" onClick={() => setOpen(true)}><UserCog size={13} aria-hidden />Status</Button>
      {open ? <StatusDrawer entity={entity} onClose={() => setOpen(false)} /> : null}
    </>);
}

/** The brief's Active / Inactive switch (§4.5): one click opens the status form for tomorrow, with its reason and dates;
 *  nothing changes until it is saved there. */
export function CustomerStatusSwitch({ entity }: { entity: Customer360 }) {
  const { can } = useSession();
  const [open, setOpen] = useState(false);
  const current = entity.status_detail?.status ?? entity.status;
  const on = current === 'ACTIVE' || current === 'STARTING';
  const may = can('customer.status.manage') && entity.status !== 'RETIRED';
  return (
    <>
      <button type="button" role="switch" aria-checked={on} disabled={!may} onClick={() => setOpen(true)}
        className="flex h-9 w-full items-center gap-2 rounded-full border border-line bg-surface pl-1 pr-4 text-body font-medium text-ink hover:bg-surface-sunken disabled:opacity-60">
        <span aria-hidden className={on ? 'size-7 rounded-full bg-brand' : 'size-7 rounded-full bg-line-strong'} />
        {on ? (current === 'STARTING' ? 'Starting' : 'Active') : current === 'PAUSED' ? 'Paused' : 'Inactive'}
      </button>
      {open ? <StatusDrawer entity={entity} onClose={() => setOpen(false)} /> : null}
    </>);
}

function StatusDrawer({ entity, onClose }: { entity: Customer360; onClose: () => void }) {
  const qc = useQueryClient();
  const today = istToday();
  const current = entity.status_detail?.status ?? entity.status;
  const [to, setTo] = useState<Target>(current === 'PAUSED' || current === 'LEFT' || current === 'INACTIVE' ? 'ACTIVE' : 'PAUSED');
  const [from, setFrom] = useState(addDays(today, 1));
  const [until, setUntil] = useState(addDays(today, 7));
  const [openEnded, setOpenEnded] = useState(false);
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [key] = useState(() => crypto.randomUUID());
  const left = useReasonCodes('CUSTOMER_LEFT');
  const change = useReasonCodes('CUSTOMER_CHANGE_REASON');
  const list = (to === 'LEFT' ? left.data : change.data) ?? [];
  const reason = list.find((r) => r.code === reasonCode);
  const noteOk = note.trim().length >= 3;
  const ok = from >= today
    && (to !== 'LEFT' || (Boolean(reason) && (!reason!.requires_comment || noteOk)))
    && (to !== 'PAUSED' || ((Boolean(reason) || noteOk) && (openEnded || until >= from)));
  const save = useMutation({
    mutationFn: () => rpc<{ status: string; detail: string; note?: string }>('set_customer_status', {
      p_customer_id: entity.id, p_status: to, p_from: from, p_until: to === 'PAUSED' && !openEnded ? until : null,
      p_reason_code: reasonCode || null, p_note: note.trim() || null, p_idempotency_key: key }),
    onSuccess: async (r) => {
      toast(r.note ? `${r.detail} ${r.note}` : r.detail, { tone: 'ok' });
      onClose();
      await refreshCustomer(qc, entity.id);
    },
  });
  const pick = (t: Target) => { setTo(t); setReasonCode(''); save.reset(); };
  return (
    <Drawer open onClose={onClose} width="min(520px, 100vw)" title={`Status · ${entity.name}`}
      footer={<Footer onClose={onClose} disabled={!ok} pending={save.isPending} label={from === today ? 'Apply today' : 'Schedule'} />}>
      <Form onSubmit={() => ok && save.mutate()}>
        <div className="flex flex-wrap items-center gap-2 text-small text-ink-muted">Now: <CustomerStatusChips entity={{ ...entity, unpaid: null }} /></div>
        <Choice label="New status" value={to} onChange={pick} options={[['ACTIVE', 'Active'], ['PAUSED', 'Pause'], ['LEFT', 'Left Desigo']] as const} />
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={to === 'LEFT' ? 'Left on' : 'From'} required hint={from === today ? 'Today: it applies at once, and today\'s round follows if it can.' : undefined}>
            <Input type="date" min={today} value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          {to === 'PAUSED' ? (
            <Field label="Until (the last paused day)" hint={openEnded ? 'Until further notice: resume with Status › Active.' : `Delivery resumes on ${dm(addDays(until, 1))}.`}>
              <Input type="date" min={from} value={until} disabled={openEnded} onChange={(e) => setUntil(e.target.value)} />
            </Field>) : null}
        </div>
        {to === 'PAUSED' ? <Checkbox checked={openEnded} onChange={(e) => setOpenEnded(e.target.checked)} label="Until further notice" /> : null}
        <Field label={to === 'LEFT' ? 'Why they left' : 'Reason'} required={to === 'LEFT'}
          hint={to === 'PAUSED' ? 'A reason from the list or a note.' : to === 'LEFT' ? 'The rider collects the bottles once; those not back after 7 days are charged on the bill.' : undefined}>
          <Select value={reasonCode} onChange={(e) => setReasonCode(e.target.value)} placeholder={to === 'LEFT' ? 'Choose…' : 'None from the list'}
            options={list.map((r) => ({ value: r.code, label: r.label_en }))} />
        </Field>
        <Field label="Note" required={Boolean(reason?.requires_comment)}>
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={reason?.requires_comment ? 'Say why (needed for Other)' : 'e.g. away for Diwali; asked on WhatsApp'} />
        </Field>
        <Outcome error={save.error} />
      </Form>
    </Drawer>);
}

/* ── The standing arrangement: items, days, and what changes from a date ─────────────────────────────── */

/** On Overview › Standing arrangement: the days it delivers, the two dated changes, and the changes waiting for their date. */
export function ArrangementManage({ entity }: { entity: Customer360 }) {
  const { can } = useSession();
  const openTab = useOpenTab();
  const [open, setOpen] = useState<'variant' | 'pattern' | 'items' | null>(null);
  const may = can('customer.subscription.manage') && (entity.subscriptions ?? []).length > 0;
  const p = entity.pending_changes;
  const doorCode = (id: string | null) => entity.locations.find((l) => l.id === id)?.code ?? 'a new door';
  const pending = [
    ...(p?.agreements ?? []).map((a) => ({ from: a.from, key: `a${a.subscriptionId}`,
      text: `${a.lines.map((l) => `${l.quantity} × ${l.variant}`).join(' · ') || 'nothing'} · ${patternLabel(a.pattern, a.weekdays, a.lines)}${a.doorChanged ? ` · to door ${a.door ?? '—'}` : ''}` })),
    ...(p?.doors ?? []).map((d) => ({ from: d.from, key: `d${d.moveId}`, text: `delivered to ${doorCode(d.doorId)}${d.closesDoorId ? `; ${doorCode(d.closesDoorId)} closes` : ''}` })),
    ...(p?.statuses ?? []).map((s) => ({ from: s.from, key: `s${s.id}`,
      text: s.status === 'LEFT' ? 'leaves Desigo' : s.status === 'PAUSED' ? `paused${s.until ? ` until ${dm(s.until)}` : ' until further notice'}` : s.status === 'ACTIVE' ? 'delivery resumes' : s.status.toLowerCase() })),
  ].sort((x, y) => x.from.localeCompare(y.from));
  return (
    <div className="mt-2 grid gap-2 border-t border-line pt-2">
      <p className="text-small text-ink-muted">Days: <span className="text-ink">{entity.pattern ? patternLabel(entity.pattern.pattern, entity.pattern.weekdays) : '—'}</span></p>
      {pending.length > 0 ? (
        <div>
          <div className="label-text">Changes waiting for their date</div>
          <ul className="mt-1 grid gap-0.5 text-small">
            {pending.map((x) => <li key={x.key} className="flex gap-2"><CalendarClock size={13} className="mt-0.5 shrink-0 text-queued" aria-hidden /><span><span className="font-medium">From {dm(x.from)}:</span> {x.text}</span></li>)}
          </ul>
        </div>) : null}
      {may ? <p className="text-small text-ink-muted">Amend, exceptions and Rejoin are together in <button type="button" className="font-medium text-brand hover:underline" onClick={() => openTab('subscription', 'agreement')}>Subscription › Agreement</button>.</p> : null}
      {may ? (
        <div className="flex flex-wrap gap-2">
          <Button size="dense" onClick={() => setOpen('variant')}><Repeat size={13} aria-hidden />Change items from a date</Button>
          <Button size="dense" onClick={() => setOpen('pattern')}><CalendarClock size={13} aria-hidden />Change days</Button>
          <Button size="dense" onClick={() => setOpen('items')}><CalendarClock size={13} aria-hidden />Each item its own days</Button>
        </div>) : null}
      {open === 'variant' ? <VariantDrawer entity={entity} onClose={() => setOpen(null)} /> : null}
      {open === 'items' ? <ItemsDaysDrawer entity={entity} onClose={() => setOpen(null)} /> : null}
      {open === 'pattern' ? <PatternDrawer entity={entity} onClose={() => setOpen(null)} /> : null}
    </div>);
}

interface ItemDays { variantId: string; quantity: number; every: Every; weekdays: number[] }

/** Each item its own days from a date (141500; the owner, 2026-10-24: "1 L of G daily and 1 HG on alternate days").
 *  Starts from what the coming week brings: an item every day is every day, every second day is alternate days, else the
 *  weekdays it comes on. */
function ItemsDaysDrawer({ entity, onClose }: { entity: Customer360; onClose: () => void }) {
  const qc = useQueryClient();
  const first = useFirstOpenDay();
  const [from, setFrom] = useState(first);
  const [items, setItems] = useState<ItemDays[] | null>(null);
  const [sub, setSub] = useState('');
  const [reason, setReason] = useState('');
  const idem = useIdempotencyKey();
  const variants = useVariants();
  const week = useCalendarRange(entity.id, from, addDays(from, 6));
  const catalogue = variants.data ?? [];
  const guessed: ItemDays[] = (() => {
    const days = week.data?.days ?? [];
    const codes = [...new Set(days.flatMap((d) => d.planned.map((q) => q.variant)))];
    return codes.map((code) => {
      const on = days.map((d) => d.planned.find((q) => q.variant === code)?.quantity ?? 0);
      const quantity = Math.max(...on);
      const every: Every = on.every((n) => n > 0) ? 'DAILY' : on.every((n, i) => (n > 0) === (i % 2 === on.findIndex((x) => x > 0) % 2)) ? 'ALTERNATE' : 'WEEKDAYS';
      return { variantId: catalogue.find((v) => v.code === code)?.id ?? '', quantity, every,
        weekdays: days.filter((_, i) => on[i]! > 0).map((d) => isoWeekday(d.date)).sort() };
    });
  })();
  const shown = items ?? guessed;
  const set = (i: number, patch: Partial<ItemDays>) => setItems(shown.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const ids = shown.map((l) => l.variantId);
  const needsSub = inForce(entity, from).length > 1;
  const ok = from > istToday() && shown.length > 0 && shown.every((l) => l.variantId && l.quantity >= 1 && l.quantity <= 50 && (l.every !== 'WEEKDAYS' || l.weekdays.length > 0))
    && new Set(ids).size === ids.length && reason.trim().length >= 3 && (!needsSub || Boolean(sub));
  const save = useMutation({
    mutationFn: async () => {
      const args = { p_customer_id: entity.id, p_from: from, p_items: shown.map((l) => ({ variantId: l.variantId, quantity: l.quantity, every: l.every, weekdays: l.every === 'WEEKDAYS' ? l.weekdays : null })),
        p_reason: reason.trim(), p_subscription_id: needsSub ? sub : null };
      return rpc<{ detail?: string; from: string }>('set_customer_items_plan', { ...args, p_idempotency_key: await idem.keyFor(args) });
    },
    onSuccess: async (r) => { idem.renew(); toast(r.detail ?? `Scheduled from ${dm(r.from)}.`, { tone: 'ok' }); onClose(); await refreshCustomer(qc, entity.id); },
  });
  return (
    <Drawer open onClose={onClose} width="min(620px, 100vw)" title={`Each item its own days · ${entity.name}`}
      footer={<Footer onClose={onClose} disabled={!ok} pending={save.isPending} label="Schedule" />}>
      <Form onSubmit={() => ok && save.mutate()}>
        <Field label="From" required hint="The agreement changes from this day on; alternate days count from it. Days before it keep what they have.">
          <Input type="date" min={addDays(istToday(), 1)} value={from} onChange={(e) => { setFrom(e.target.value); setItems(null); save.reset(); }} />
        </Field>
        <AgreementPick entity={entity} day={from} value={sub} onChange={setSub} />
        <div className="grid gap-2">
          <div className="label-text">Items and their days</div>
          {shown.map((l, i) => (
            <div key={i} className="grid gap-1 rounded-[var(--radius-control)] border border-line p-2">
              <div className="grid grid-cols-[minmax(0,1fr)_5rem_9rem_auto] items-end gap-2">
                <Select aria-label="Item" value={l.variantId} onChange={(e) => set(i, { variantId: e.target.value })} placeholder="Choose an item…"
                  options={catalogue.map((v) => ({ value: v.id, label: v.code }))} />
                <Input aria-label="Each time" type="number" min={1} max={50} value={l.quantity} onChange={(e) => set(i, { quantity: Number(e.target.value) })} />
                <Select aria-label="Days" value={l.every} onChange={(e) => set(i, { every: e.target.value as Every })} options={EVERY_OPTIONS} />
                <Button size="dense" variant="ghost" aria-label="Remove" disabled={shown.length === 1} onClick={() => setItems(shown.filter((_, j) => j !== i))}><Trash2 size={13} /></Button>
              </div>
              {l.every === 'WEEKDAYS' ? (
                <div role="group" aria-label="Weekdays" className="flex flex-wrap gap-1">
                  {WEEKDAYS.map((w, k) => (
                    <button key={w} type="button" aria-pressed={l.weekdays.includes(k + 1)}
                      onClick={() => set(i, { weekdays: l.weekdays.includes(k + 1) ? l.weekdays.filter((x) => x !== k + 1) : [...l.weekdays, k + 1].sort() })}
                      className={cx('h-7 rounded-full border px-2.5 text-small', l.weekdays.includes(k + 1) ? 'border-brand bg-brand-soft text-brand' : 'border-line text-ink-muted')}>{w}</button>))}
                </div>) : null}
            </div>))}
          <div><Button size="dense" onClick={() => setItems([...shown, { variantId: '', quantity: 1, every: 'DAILY', weekdays: [] }])}><Plus size={13} aria-hidden />Another item</Button></div>
          {ok ? <p className="text-small text-ink-muted">From {dm(from)}: {shown.map((l) => `${l.quantity} ${catalogue.find((v) => v.id === l.variantId)?.code ?? ''} ${everyLabel(l.every, l.weekdays)}`).join(' · ')}</p> : null}
        </div>
        <Field label="Why" required><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. HG only on alternate days, asked on WhatsApp" /></Field>
        <Outcome error={save.error} />
      </Form>
    </Drawer>);
}

function VariantDrawer({ entity, onClose }: { entity: Customer360; onClose: () => void }) {
  const qc = useQueryClient();
  const first = useFirstOpenDay();
  const [from, setFrom] = useState(first);
  const [lines, setLines] = useState<{ variantId: string; quantity: number }[] | null>(null);
  const [sub, setSub] = useState('');
  const [reason, setReason] = useState('');
  const [key] = useState(() => crypto.randomUUID());
  const variants = useVariants();
  const plan = useCalendarRange(entity.id, from, from);
  const catalogue = variants.data ?? [];
  // Until edited: what the agreement plans for that day.
  const planned = (plan.data?.days[0]?.planned ?? []).map((q) => ({ variantId: catalogue.find((v) => v.code === q.variant)?.id ?? '', quantity: q.quantity }));
  const shown = lines ?? planned;
  const set = (next: { variantId: string; quantity: number }[]) => setLines(next);
  const ids = shown.map((l) => l.variantId);
  const needsSub = inForce(entity, from).length > 1;
  const ok = from > istToday() && shown.length > 0 && shown.every((l) => l.variantId && l.quantity >= 1 && l.quantity <= 50)
    && new Set(ids).size === ids.length && reason.trim().length >= 3 && (!needsSub || Boolean(sub));
  const save = useMutation({
    mutationFn: () => rpc<{ detail?: string; from: string }>('change_customer_variant', { p_customer_id: entity.id, p_from: from, p_lines: shown, p_reason: reason.trim(),
      p_idempotency_key: key, p_subscription_id: needsSub ? sub : null }),
    onSuccess: async (r) => { toast(r.detail ?? `Scheduled from ${dm(r.from)}.`, { tone: 'ok' }); onClose(); await refreshCustomer(qc, entity.id); },
  });
  return (
    <Drawer open onClose={onClose} width="min(560px, 100vw)" title={`Items from a date · ${entity.name}`}
      footer={<Footer onClose={onClose} disabled={!ok} pending={save.isPending} label="Schedule" />}>
      <Form onSubmit={() => ok && save.mutate()}>
        <Field label="From" required hint="The agreement changes from this day on; days before it keep what they have. The day must still be before its cut-off.">
          <Input type="date" min={addDays(istToday(), 1)} value={from} onChange={(e) => { setFrom(e.target.value); save.reset(); }} />
        </Field>
        <AgreementPick entity={entity} day={from} value={sub} onChange={setSub} />
        <div className="grid gap-2">
          <div className="label-text">Each delivery brings</div>
          {shown.map((l, i) => (
            <div key={i} className="flex items-end gap-2">
              <div className="min-w-0 flex-1">
                <Select aria-label="Item" value={l.variantId} onChange={(e) => set(shown.map((x, j) => (j === i ? { ...x, variantId: e.target.value } : x)))} placeholder="Choose an item…"
                  options={catalogue.map((v) => ({ value: v.id, label: `${v.code} · ${v.name}`, disabled: ids.includes(v.id) && v.id !== l.variantId }))} />
              </div>
              <Input aria-label="Units" type="number" min={1} max={50} className="w-20" value={l.quantity}
                onChange={(e) => set(shown.map((x, j) => (j === i ? { ...x, quantity: Math.max(0, Math.round(Number(e.target.value) || 0)) } : x)))} />
              <Button size="dense" variant="ghost" aria-label="Remove this item" onClick={() => set(shown.filter((_, j) => j !== i))}><Trash2 size={13} aria-hidden /></Button>
            </div>))}
          <div><Button size="dense" onClick={() => set([...shown, { variantId: '', quantity: 1 }])}><Plus size={13} aria-hidden />Add an item</Button></div>
          {plan.isFetching && lines === null ? <p className="text-small text-ink-muted">Reading what that day plans…</p> : null}
          {shown.length === 0 && !plan.isFetching ? <p className="text-small text-ink-muted">At least one item. To stop delivering, use Status › Pause or Left Desigo.</p> : null}
        </div>
        <Field label="Why" required><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. asked for 1 L instead of 500 ml from the 1st" /></Field>
        <Outcome error={save.error} />
      </Form>
    </Drawer>);
}

function PatternDrawer({ entity, onClose }: { entity: Customer360; onClose: () => void }) {
  const qc = useQueryClient();
  const first = useFirstOpenDay();
  const now = entity.pattern;
  const [pattern, setPattern] = useState<'DAILY' | 'ALTERNATE' | 'WEEKDAYS'>(now?.pattern === 'ALTERNATE' || now?.pattern === 'WEEKDAYS' ? now.pattern : 'DAILY');
  const [weekdays, setWeekdays] = useState<number[]>(now?.weekdays ?? []);
  const [from, setFrom] = useState(first);
  const [sub, setSub] = useState('');
  const [reason, setReason] = useState('');
  const [key] = useState(() => crypto.randomUUID());
  const needsSub = inForce(entity, from).length > 1;
  const ok = from > istToday() && (pattern !== 'WEEKDAYS' || weekdays.length > 0) && reason.trim().length >= 3 && (!needsSub || Boolean(sub));
  const save = useMutation({
    mutationFn: () => rpc<{ detail?: string; from: string }>('set_customer_pattern', { p_customer_id: entity.id, p_pattern: pattern,
      p_weekdays: pattern === 'WEEKDAYS' ? [...weekdays].sort() : null, p_from: from, p_reason: reason.trim(), p_idempotency_key: key, p_subscription_id: needsSub ? sub : null }),
    onSuccess: async (r) => { toast(r.detail ?? `Scheduled from ${dm(r.from)}.`, { tone: 'ok' }); onClose(); await refreshCustomer(qc, entity.id); },
  });
  return (
    <Drawer open onClose={onClose} width="min(520px, 100vw)" title={`Days from a date · ${entity.name}`}
      footer={<Footer onClose={onClose} disabled={!ok} pending={save.isPending} label="Schedule" />}>
      <Form onSubmit={() => ok && save.mutate()}>
        <p className="text-small text-ink-muted">Now: {now ? patternLabel(now.pattern, now.weekdays) : '—'}</p>
        <Choice label="Delivered" value={pattern} onChange={(v) => { setPattern(v); save.reset(); }} options={[['DAILY', 'Every day'], ['ALTERNATE', 'Alternate days'], ['WEEKDAYS', 'Chosen weekdays']] as const} />
        {pattern === 'WEEKDAYS' ? (
          <div role="group" aria-label="Weekdays" className="flex flex-wrap gap-1">
            {WEEKDAYS.map((w, i) => (
              <button key={w} type="button" aria-pressed={weekdays.includes(i + 1)} onClick={() => setWeekdays(weekdays.includes(i + 1) ? weekdays.filter((x) => x !== i + 1) : [...weekdays, i + 1])}
                className={cx('h-7 rounded-full border px-2.5 text-small', weekdays.includes(i + 1) ? 'border-brand bg-brand-soft text-brand' : 'border-line text-ink-muted')}>{w}</button>))}
          </div>) : null}
        <Field label="From" required hint={pattern === 'ALTERNATE' ? 'The first delivery is on this day, then every second day.' : 'Days before it keep what they have. The day must still be before its cut-off.'}>
          <Input type="date" min={addDays(istToday(), 1)} value={from} onChange={(e) => { setFrom(e.target.value); save.reset(); }} />
        </Field>
        <AgreementPick entity={entity} day={from} value={sub} onChange={setSub} />
        <Field label="Why" required><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. only weekends from November" /></Field>
        <Outcome error={save.error} />
      </Form>
    </Drawer>);
}

/* ── Door: add or move from a date ───────────────────────────────────────────────────────────────────── */

export function MoveDoorButton({ entity }: { entity: Customer360 }) {
  const { can } = useSession();
  const [open, setOpen] = useState(false);
  if (!can('customer.edit') || entity.status === 'RETIRED') return null;
  return (
    <>
      <Button size="dense" onClick={() => setOpen(true)}><MapPinPlus size={13} aria-hidden />Add / move address</Button>
      {open ? <MoveDoorDrawer entity={entity} onClose={() => setOpen(false)} /> : null}
    </>);
}

function MoveDoorDrawer({ entity, onClose }: { entity: Customer360; onClose: () => void }) {
  const qc = useQueryClient();
  const first = useFirstOpenDay();
  const doors = entity.locations.filter((l) => l.status === 'ACTIVE');
  const usual = doors.find((l) => l.is_primary) ?? doors[0];
  const [door, setDoor] = useState({ label: '', addressLine: '', area: '', pincode: '', landmark: '', instructions: '', lat: '', lng: '' });
  const [routeId, setRouteId] = useState(entity.route?.id ?? '');
  const [from, setFrom] = useState(first);
  const [close, setClose] = useState(Boolean(usual));
  const [closeId, setCloseId] = useState(usual?.id ?? '');
  const [sub, setSub] = useState('');
  const [reason, setReason] = useState('');
  const [key] = useState(() => crypto.randomUUID());
  const routes = useQuery({ queryKey: ['routes-list'], queryFn: async () => {
    const { data, error } = await supabase.from('routes').select('id,code,name,status').order('code');
    if (error) throw error;
    return (data ?? []) as { id: string; code: string; name: string; status: string }[];
  } });
  const put = (k: keyof typeof door) => (e: { target: { value: string } }) => setDoor({ ...door, [k]: e.target.value });
  const pinOk = (door.lat.trim() === '') === (door.lng.trim() === '') && (door.lat.trim() === '' || (Number.isFinite(Number(door.lat)) && Number.isFinite(Number(door.lng))));
  const needsSub = !close && inForce(entity, from).length > 1;
  const ok = from > istToday() && door.addressLine.trim().length >= 5 && pinOk && reason.trim().length >= 3 && (!close || Boolean(closeId)) && (!needsSub || Boolean(sub));
  const save = useMutation({
    mutationFn: () => rpc<{ detail: string }>('move_customer_door', {
      p_customer_id: entity.id,
      p_new_door: { label: door.label.trim() || null, addressLine: door.addressLine.trim(), area: door.area.trim() || null, pincode: door.pincode.trim() || null,
        landmark: door.landmark.trim() || null, instructions: door.instructions.trim() || null, lat: door.lat.trim() || null, lng: door.lng.trim() || null, routeId: routeId || null },
      p_from: from, p_close_door: close ? closeId : null, p_reason: reason.trim(), p_idempotency_key: key, p_subscription_id: needsSub ? sub : null }),
    onSuccess: async (r) => { toast(r.detail, { tone: 'ok' }); onClose(); await refreshCustomer(qc, entity.id); },
  });
  return (
    <Drawer open onClose={onClose} width="min(600px, 100vw)" title={`Add / move address · ${entity.name}`}
      footer={<Footer onClose={onClose} disabled={!ok} pending={save.isPending} label="Schedule" />}>
      <Form onSubmit={() => ok && save.mutate()}>
        <p className="text-small text-ink-muted">The new door is added now; deliveries go to it from the date below. The rider's packet changes from that day.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Which door"><Input value={door.label} onChange={put('label')} placeholder="Home, Office, Shop…" /></Field>
          <Field label="Round from that day">
            <Select value={routeId} onChange={(e) => setRouteId(e.target.value)} placeholder="Not on a round"
              options={(routes.data ?? []).filter((r) => r.status === 'ACTIVE' || r.id === routeId).map((r) => ({ value: r.id, label: `${r.code} · ${r.name}` }))} />
          </Field>
          <Field label="Address" required className="sm:col-span-2"><Input value={door.addressLine} onChange={put('addressLine')} placeholder="House, street — so a rider can find it" /></Field>
          <Field label="Area"><Input value={door.area} onChange={put('area')} /></Field>
          <Field label="Pincode"><Input inputMode="numeric" value={door.pincode} onChange={put('pincode')} /></Field>
          <Field label="Landmark"><Input value={door.landmark} onChange={put('landmark')} /></Field>
          <Field label="Gate or floor notes"><Input value={door.instructions} onChange={put('instructions')} /></Field>
          <Field label="Latitude" error={pinOk ? null : 'A pin has both numbers'}><Input inputMode="decimal" value={door.lat} onChange={put('lat')} placeholder="26.27" /></Field>
          <Field label="Longitude"><Input inputMode="decimal" value={door.lng} onChange={put('lng')} placeholder="73.02" /></Field>
          <p className="text-small text-ink-muted sm:col-span-2">Tip: in Google Maps, long-press the gate and copy the two numbers shown. Leave both empty and the rider pins it at the gate.</p>
          <Field label="From" required hint="The day must still be before its cut-off."><Input type="date" min={addDays(istToday(), 1)} value={from} onChange={(e) => { setFrom(e.target.value); save.reset(); }} /></Field>
        </div>
        {doors.length > 0 ? (
          <div className="grid gap-2">
            <Checkbox checked={close} onChange={(e) => setClose(e.target.checked)} label="Close the current door from that day" description="It is kept on record, never deleted; its agreements move to the new door." />
            {close && doors.length > 1 ? (
              <Field label="Door to close"><Select value={closeId} onChange={(e) => setCloseId(e.target.value)} placeholder="Choose…"
                options={doors.map((l) => ({ value: l.id, label: `${l.code}${l.label ? ` · ${l.label}` : ''}${l.is_primary ? ' (usual)' : ''}` }))} /></Field>) : null}
          </div>) : null}
        {needsSub ? <AgreementPick entity={entity} day={from} value={sub} onChange={setSub} /> : null}
        <Field label="Why" required><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. moved house on the 15th" /></Field>
        <Outcome error={save.error} />
      </Form>
    </Drawer>);
}

/* ── Money › Discounts ───────────────────────────────────────────────────────────────────────────────── */

const discountWords = (d: CustomerDiscount) => (d.kind === 'BILL_PERCENT' ? `${Number(d.percent)}% off the bill` : `${inr(d.amountPaise)} off each ${d.variant ?? 'item'}`);

export function CustomerDiscounts({ entity }: { entity: Customer360 }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const may = can('customer.discount.manage');
  const [adding, setAdding] = useState(false);
  const [ending, setEnding] = useState<CustomerDiscount | null>(null);
  const [until, setUntil] = useState(istToday());
  const [endReason, setEndReason] = useState('');
  const [endKey, setEndKey] = useState(() => crypto.randomUUID());
  const list = entity.discounts ?? [];
  const end = useMutation({
    mutationFn: () => rpc<{ detail: string }>('end_customer_discount', { p_discount_id: ending!.id, p_until: until, p_reason: endReason.trim(), p_idempotency_key: endKey }),
    onSuccess: async (r) => { toast(r.detail, { tone: 'ok' }); setEnding(null); await refreshCustomer(qc, entity.id); },
  });
  const startEnd = (d: CustomerDiscount) => { setEnding(d); setUntil(istToday()); setEndReason(''); setEndKey(crypto.randomUUID()); end.reset(); };
  return (
    <section className="panel rounded-[var(--radius-panel)] border border-line bg-surface">
      <header className="flex items-center justify-between gap-4 border-b border-line px-4 py-2.5">
        <div className="min-w-0">
          <h2 className="text-section">Discounts</h2>
          <p className="mt-0.5 text-small text-ink-muted">₹ off each unit of an item, or a percent off the bill, from a date. Never deleted: a discount is ended, or withdrawn before it begins.</p>
        </div>
        {may ? <Button size="dense" variant="primary" onClick={() => setAdding(true)}><Plus size={13} aria-hidden />Add a discount</Button> : null}
      </header>
      {list.length === 0 ? <p className="px-4 py-3 text-small text-ink-muted">No discount, live or scheduled.</p> : (
        <ul className="divide-y divide-line text-small">
          {list.map((d) => (
            <li key={d.id} className="flex flex-wrap items-center gap-3 px-4 py-2">
              <Percent size={14} className="shrink-0 text-ink-faint" aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-ink">{discountWords(d)}</div>
                <div className="text-ink-muted">{d.until ? `${dm(d.from)} – ${dm(d.until)}` : `from ${dm(d.from)}, no end date`} · {d.reason}</div>
              </div>
              <StatusBadge tone={d.state === 'LIVE' ? 'accepted' : 'info'} icon={d.state === 'LIVE' ? Percent : CalendarClock}>{d.state === 'LIVE' ? 'live' : 'scheduled'}</StatusBadge>
              {may ? <Button size="dense" onClick={() => startEnd(d)}>{d.state === 'SCHEDULED' ? 'Withdraw' : 'End'}</Button> : null}
            </li>))}
        </ul>)}
      {adding ? <DiscountDrawer entity={entity} onClose={() => setAdding(false)} /> : null}
      <Dialog open={Boolean(ending)} onClose={() => setEnding(null)} title={ending?.state === 'SCHEDULED' ? 'Withdraw this discount' : 'End this discount'}
        footer={<div className="flex justify-end gap-2"><Button onClick={() => setEnding(null)}>Cancel</Button>
          <Button variant="primary" disabled={endReason.trim().length < 3 || !until || end.isPending} onClick={() => end.mutate()}>{end.isPending ? 'Working…' : ending?.state === 'SCHEDULED' ? 'Withdraw' : 'End'}</Button></div>}>
        <div className="grid gap-3">
          <p className="text-body">{ending ? discountWords(ending) : ''}. {ending?.state === 'SCHEDULED' ? 'It has not begun: withdrawn, it is kept and never applies.' : 'It applies up to and including the last day below.'}</p>
          {ending?.state === 'LIVE' ? <Field label="Last day it applies"><Input type="date" value={until} min={istToday().slice(0, 8) + '01'} onChange={(e) => setUntil(e.target.value)} /></Field> : null}
          <Field label="Why" required><Input value={endReason} onChange={(e) => setEndReason(e.target.value)} placeholder="e.g. the festival offer is over" /></Field>
          <Outcome error={end.error} />
        </div>
      </Dialog>
    </section>);
}

function DiscountDrawer({ entity, onClose }: { entity: Customer360; onClose: () => void }) {
  const qc = useQueryClient();
  const today = istToday();
  const [kind, setKind] = useState<'VARIANT_RUPEES' | 'BILL_PERCENT'>('VARIANT_RUPEES');
  const [variantId, setVariantId] = useState('');
  const [rupees, setRupees] = useState('');
  const [percent, setPercent] = useState('');
  const [from, setFrom] = useState(today);
  const [until, setUntil] = useState('');
  const [reason, setReason] = useState('');
  const [key] = useState(() => crypto.randomUUID());
  const variants = useVariants();
  const taken = new Set(entity.products.filter((p) => p.takes || p.assigned).map((p) => p.variant_code));
  const catalogue = [...(variants.data ?? [])].sort((a, b) => Number(taken.has(b.code)) - Number(taken.has(a.code)) || a.code.localeCompare(b.code));
  const paise = Math.round(Number(rupees) * 100);
  const pct = Number(percent);
  const ok = from >= `${today.slice(0, 7)}-01` && (!until || until >= from) && reason.trim().length >= 3
    && (kind === 'VARIANT_RUPEES' ? Boolean(variantId) && paise > 0 : pct > 0 && pct <= 100);
  const save = useMutation({
    mutationFn: () => rpc<{ detail: string }>('set_customer_discount', { p_customer_id: entity.id, p_kind: kind,
      p_variant_id: kind === 'VARIANT_RUPEES' ? variantId : null, p_amount_paise: kind === 'VARIANT_RUPEES' ? paise : null, p_percent: kind === 'BILL_PERCENT' ? pct : null,
      p_from: from, p_until: until || null, p_reason: reason.trim(), p_idempotency_key: key }),
    onSuccess: async (r) => { toast(r.detail, { tone: 'ok' }); onClose(); await refreshCustomer(qc, entity.id); },
  });
  return (
    <Drawer open onClose={onClose} width="min(520px, 100vw)" title={`Add a discount · ${entity.name}`}
      footer={<Footer onClose={onClose} disabled={!ok} pending={save.isPending} label="Give discount" />}>
      <Form onSubmit={() => ok && save.mutate()}>
        <Choice label="Kind" value={kind} onChange={(v) => { setKind(v); save.reset(); }} options={[['VARIANT_RUPEES', '₹ off an item'], ['BILL_PERCENT', '% off the bill']] as const} />
        {kind === 'VARIANT_RUPEES' ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Item" required><Select value={variantId} onChange={(e) => setVariantId(e.target.value)} placeholder="Choose…"
              options={catalogue.map((v) => ({ value: v.id, label: `${v.code} · ${v.name}${taken.has(v.code) ? '' : ' (not taken)'}` }))} /></Field>
            <Field label="₹ off each unit" required hint="Never below zero: a unit is priced at the price that day less this."><Input inputMode="decimal" value={rupees} onChange={(e) => setRupees(e.target.value)} placeholder="2" /></Field>
          </div>
        ) : (
          <Field label="Percent off the bill" required hint="Taken off each delivery day's amount while it is in force."><Input inputMode="decimal" value={percent} onChange={(e) => setPercent(e.target.value)} placeholder="5" /></Field>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="From" required hint="This month or later; a billed month is not re-priced."><Input type="date" min={`${today.slice(0, 7)}-01`} value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
          <Field label="Until (optional)" hint="Empty: until ended."><Input type="date" min={from} value={until} onChange={(e) => setUntil(e.target.value)} /></Field>
        </div>
        <p className="text-small text-ink-muted">One of each kind (per item) at a time: one already in force ends the day before this one starts.</p>
        <Field label="Why" required><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. loyal customer, three years" /></Field>
        <Outcome error={save.error} />
      </Form>
    </Drawer>);
}
